<template>
    <div class="user-panel">
        <div class="object-titlebox">
            <table class="prompt-dialog-table">
                <tbody>
                    <tr>
                        <td class="prompt-title-left">
                            <span class="keyvalut-header-text" v-t="'message.item_ai_prompt_setting'"></span>
                            <el-tooltip effect="dark" placement="top-start">
                                <div slot="content" class="tooltip-content" v-html="$t('message.hint_ai_prompt_setting')"></div>
                                <img class="icon-help" :src="require('@/assets/icon_help.svg')" alt="" />
                            </el-tooltip>
                        </td>
                        <td class="prompt-title-right">
                            <button class="transparent-button" type="button" @click="openCreate">
                                <img :src="require('@/assets/add_user.svg')" alt="add" />
                                <span class="normal-command-text" v-t="'message.common_action_add'"></span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="prompt-list-panel" v-loading="loading">
            <table class="prompt-table">
                <tbody>
                    <tr class="prompt-header-row">
                        <td class="prompt-header-cell name-col" v-t="'message.item_ai_prompt_name'"></td>
                        <td class="prompt-header-cell op-col" v-t="'message.common_action_edit'"></td>
                        <td class="prompt-header-cell op-col" v-t="'message.common_action_delete'"></td>
                    </tr>
                    <tr v-for="prompt in promptList" :key="prompt.id" class="prompt-list-row">
                        <td class="prompt-list-cell name-col">{{ prompt.name }}</td>
                        <td class="prompt-list-cell op-cell">
                            <button class="prompt-action-button" type="button" @click="openEdit(prompt)">
                                <img :src="require('@/assets/edit.svg')" alt="edit" />
                            </button>
                        </td>
                        <td class="prompt-list-cell op-cell">
                            <button
                                class="prompt-action-button"
                                type="button"
                                @click="openDeleteDialog(prompt)"
                            >
                                <img :src="require('@/assets/delete.svg')" alt="delete" />
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!promptList.length">
                        <td colspan="3" class="empty-row" v-t="'message.info_ai_prompt_empty'"></td>
                    </tr>
                </tbody>
            </table>
        </div>

        <el-dialog
            :title="dialogTitle"
            :visible.sync="showEditDialog"
            width="760px"
            append-to-body
            :lock-scroll="false"
            :close-on-click-modal="false"
            :show-close="false"
        >
            <div class="dialog-body">
                <el-form label-position="top">
                    <el-form-item ref="name">
                        <div slot="label" class="field-label">
                            {{ $t("message.item_ai_prompt_name") }} <span class="required" v-t="'message.item_required'"></span>
                        </div>
                        <el-input v-model="editingPrompt.name" :maxlength="MAX_NAME_LENGTH" @input="validateInputs" />
                    </el-form-item>

                    <el-form-item ref="prompt">
                        <div slot="label" class="field-label with-top">
                            {{ $t("message.item_ai_prompt_body") }} <span class="required" v-t="'message.item_required'"></span>
                        </div>
                        <div class="template-row">
                            <span class="template-label" v-t="'message.item_ai_prompt_template_select'"></span>
                            <span class="template-help" v-t="'message.hint_ai_prompt_template_copy'"></span>
                        </div>
                        <el-select v-model="selectedTemplate" class="template-select" :placeholder="$t('message.place_holder_no_selection')" @change="applyTemplate">
                            <el-option :label="$t('message.place_holder_no_selection')" value="" />
                            <el-option
                                v-for="template in templates"
                                :key="template.value"
                                :label="template.label"
                                :value="template.value"
                            />
                        </el-select>

                        <div class="prompt-input-wrap" :class="{ 'has-error': promptError }">
                            <el-input
                                type="textarea"
                                :rows="16"
                                v-model="editingPrompt.prompt"
                                :maxlength="MAX_PROMPT_LENGTH"
                                resize="none"
                                @input="validateInputs"
                            />
                        </div>
                        <div class="counter" :class="{ 'counter-error': promptError }">
                            {{ $t("message.info_ai_prompt_count", { count: promptLength, max: MAX_PROMPT_LENGTH }) }}
                        </div>
                    </el-form-item>
                </el-form>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="showEditDialog = false" v-t="'message.button_cancel'"></el-button>
                <el-button type="primary" :loading="saving" :disabled="disableSaveButton" @click="savePrompt" v-t="'message.item_save'"></el-button>
            </span>
        </el-dialog>

        <transition name="modal">
            <div v-if="showDeleteDialog" class="dialog-modal-mask">
                <div class="small-panel" @click.stop>
                    <div class="dialog-title">{{ $t('message.title_delete_object', { objectName: deleteTargetPrompt?.name || '' }) }}</div>
                    <hr class="dialog-hr">
                    <div class="confirm-body" v-html="$t('message.info_delete_object', { objectName: deleteTargetPrompt?.name || '' })"/>
                    <hr class="dialog-hr">
                    <div class="dialog-button-field">
                        <el-button class="dialog-cancel-button" @click="cancelDeleteDialog()" v-t="'message.button_cancel'"></el-button>
                        <el-button class="dialog-ok-button" @click="deletePrompt()" v-t="'message.button_ok'"></el-button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import server from "@/components/ServerApi.js";
import commonUtil from "@/components/commonutil.js";

export default {
    name: "AiPromptSetting",
    props: ['appData'],
    data() {
        return {
            MAX_PROMPT_LENGTH: 5000,
            MAX_NAME_LENGTH: 255,
            loading: false,
            saving: false,
            showEditDialog: false,
            showDeleteDialog: false,
            promptList: [],
            deleteTargetPrompt: null,
            selectedTemplate: "",
            editingPrompt: {
                id: null,
                name: "",
                prompt: "",
            },
            editingMode: "edit",
            originalPrompt: null,
            promptError: "",
            nameError: "",
            disableSaveButton: true,
            templates: [],
            aiServerBaseUrl: "",
        };
    },
    computed: {
        promptLength() {
            return (this.editingPrompt.prompt || "").length;
        },
        dialogTitle() {
            return this.editingMode === "create"
                ? this.$t("message.title_ai_prompt_add")
                : this.$t("message.title_ai_prompt_edit");
        },
    },
    methods: {
        requireAiServerBaseUrl() {
            if (!this.aiServerBaseUrl) {
                throw new Error("AI server URL is not initialized");
            }
            return this.aiServerBaseUrl;
        },
        normalizeAiServerUrl(rawUrl) {
            const trimmed = String(rawUrl || "").replace(/\/+$/, "");
            if (!trimmed) {
                throw new Error("empty ai server url");
            }
            if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
                return trimmed;
            }
            return `https://${trimmed}`;
        },
        async loadAiServerUrl() {
            const res = await server.post("/getaiserverurl", {});
            if (res.data.status !== 0) {
                throw new Error(`getaiserverurl failed status=${res.data.status}`);
            }
            this.aiServerBaseUrl = this.normalizeAiServerUrl(res.data.aiServerUrl);
        },
        getTenantId() {
            if (this.appData && this.appData.tenantList && this.appData.tenantList.length > 0) {
                return this.appData.tenantList[0].tenantId;
            }
            if (this.$root && this.$root.store && this.$root.store.tenantId) {
                return Number(this.$root.store.tenantId);
            }
            const sessionTenantId = sessionStorage.getItem("tenantId");
            return sessionTenantId ? Number(sessionTenantId) : 0;
        },
        requireTenantId() {
            const tenantId = this.getTenantId();
            if (!tenantId) {
                throw new Error("tenantId is not available");
            }
            return tenantId;
        },
        async loadPromptList() {
            this.loading = true;
            try {
                const aiServerBaseUrl = this.requireAiServerBaseUrl();
                const tenantId = this.requireTenantId();
                const res = await server.aiGet(aiServerBaseUrl, "/console/summary/prompt/list", tenantId);
                if (res.status === 200) {
                    this.promptList = (res.data.promptList || []).map((prompt) => ({
                        id: prompt.id,
                        name: prompt.name || commonUtil.getMessageArgsText(this.$i18n, "item_ai_prompt_name_with_id", { id: prompt.id }),
                        version: prompt.version,
                        prompt: prompt.prompt || "",
                    }));
                    this.templates = (res.data.templateList || []).map((template) => ({
                        value: String(template.id),
                        label: template.name,
                        text: template.prompt || "",
                    }));
                } else {
                    commonUtil.notifyMessage(
                        this,
                        "Error",
                        commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_list_failed_http", { status: res.status })
                    );
                }
            } catch (error) {
                commonUtil.putExceptionErrorMessage(this, "loadPromptList", error);
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_ai_prompt_list_failed"));
            } finally {
                this.loading = false;
            }
        },
        openEdit(prompt) {
            this.editingMode = "edit";
            this.editingPrompt = {
                id: prompt.id,
                name: prompt.name,
                prompt: prompt.prompt,
            };
            this.originalPrompt = { name: prompt.name, prompt: prompt.prompt };
            this.selectedTemplate = "";
            this.promptError = "";
            this.nameError = "";
            this.showEditDialog = true;
            this.validateInputs();
            this.$nextTick(() => {
                this.validateInputs();
            });
        },
        openCreate() {
            this.editingMode = "create";
            this.editingPrompt = {
                id: null,
                name: "",
                prompt: "",
            };
            this.originalPrompt = null;
            this.selectedTemplate = "";
            this.promptError = "";
            this.nameError = "";
            this.showEditDialog = true;
            this.validateInputs();
            this.$nextTick(() => {
                this.validateInputs();
            });
        },
        applyTemplate(value) {
            if (!value) {
                return;
            }
            const selected = this.templates.find(template => template.value === value);
            if (selected) {
                this.editingPrompt.prompt = selected.text;
                this.validateInputs();
            }
        },
        validateInputs() {
            const name = (this.editingPrompt.name || "").trim();
            if (!name) {
                this.nameError = commonUtil.getMessageText(this.$i18n, "error_ai_prompt_name_required");
            } else if ((this.editingPrompt.name || "").length > this.MAX_NAME_LENGTH) {
                this.nameError = commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_max_length", { max: this.MAX_NAME_LENGTH });
            } else {
                this.nameError = "";
            }
            if (this.$refs.name) {
                this.$refs.name.validateState = this.nameError ? "error" : "success";
                this.$refs.name.validateMessage = this.nameError;
            }

            const trimmed = (this.editingPrompt.prompt || "").trim();
            if (!trimmed) {
                this.promptError = commonUtil.getMessageText(this.$i18n, "error_ai_prompt_body_required");
            } else if ((this.editingPrompt.prompt || "").length > this.MAX_PROMPT_LENGTH) {
                this.promptError = commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_max_length", { max: this.MAX_PROMPT_LENGTH });
            } else {
                this.promptError = "";
            }
            if (this.$refs.prompt) {
                this.$refs.prompt.validateState = this.promptError ? "error" : "success";
                this.$refs.prompt.validateMessage = this.promptError;
            }

            let hasChanges = true;
            if (this.editingMode === "edit" && this.originalPrompt) {
                hasChanges =
                    (this.editingPrompt.name || "").trim() !== (this.originalPrompt.name || "").trim() ||
                    (this.editingPrompt.prompt || "").trim() !== (this.originalPrompt.prompt || "").trim();
            }
            this.disableSaveButton = !!(this.nameError || this.promptError) || !hasChanges;
        },
        openDeleteDialog(prompt) {
            if (this.promptList.length <= 1) {
                commonUtil.notifyMessage(this, "Warning", commonUtil.getMessageText(this.$i18n, "warn_ai_prompt_delete_last_forbidden"));
                return;
            }
            this.deleteTargetPrompt = prompt;
            this.showDeleteDialog = true;
        },
        cancelDeleteDialog() {
            this.showDeleteDialog = false;
            this.deleteTargetPrompt = null;
        },
        async deletePrompt() {
            if (!this.deleteTargetPrompt) {
                return;
            }

            if (this.promptList.length <= 1) {
                this.cancelDeleteDialog();
                commonUtil.notifyMessage(this, "Warning", commonUtil.getMessageText(this.$i18n, "warn_ai_prompt_delete_last_forbidden"));
                return;
            }

            this.loading = true;
            try {
                const aiServerBaseUrl = this.requireAiServerBaseUrl();
                const tenantId = this.requireTenantId();
                const res = await server.aiPost(
                    aiServerBaseUrl,
                    "/console/summary/prompt/delete",
                    { id: this.deleteTargetPrompt.id },
                    tenantId
                );
                this.cancelDeleteDialog();
                await this.loadPromptList();
                commonUtil.notifyMessage(this, "Success", commonUtil.getMessageText(this.$i18n, "info_ai_prompt_deleted"));
            } catch (error) {
                commonUtil.putExceptionErrorMessage(this, "deletePrompt", error);
                const status = error && error.response && error.response.status;
                if (status === 409) {
                    this.cancelDeleteDialog();
                    commonUtil.notifyMessage(this, "Warning", commonUtil.getMessageText(this.$i18n, "warn_ai_prompt_delete_last_forbidden"));
                    return;
                }
                if (status === 404) {
                    this.cancelDeleteDialog();
                    commonUtil.notifyMessage(this, "Warning", commonUtil.getMessageText(this.$i18n, "warn_ai_prompt_not_found"));
                    await this.loadPromptList();
                    return;
                }
                if (status) {
                    commonUtil.notifyMessage(
                        this,
                        "Error",
                        commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_delete_failed_http", { status })
                    );
                    return;
                }
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_ai_prompt_delete_failed"));
            } finally {
                this.loading = false;
            }
        },
        async savePrompt() {
            this.validateInputs();
            if (this.nameError || this.promptError) {
                return;
            }

            this.saving = true;
            try {
                const aiServerBaseUrl = this.requireAiServerBaseUrl();
                const tenantId = this.requireTenantId();
                const requestBody = {
                    name: this.editingPrompt.name.trim(),
                    prompt: this.editingPrompt.prompt.trim(),
                };
                const endpoint = this.editingMode === "create"
                    ? "/console/summary/prompt/create"
                    : "/console/summary/prompt/modify";
                if (this.editingMode !== "create") {
                    requestBody.id = this.editingPrompt.id;
                }
                const res = await server.aiPost(aiServerBaseUrl, endpoint, requestBody, tenantId);
                if (res.status === 200) {
                    this.showEditDialog = false;
                    await this.loadPromptList();
                    commonUtil.notifyMessage(
                        this,
                        "Success",
                        commonUtil.getMessageText(
                            this.$i18n,
                            this.editingMode === "create" ? "info_ai_prompt_created" : "info_ai_prompt_saved"
                        )
                    );
                } else {
                    commonUtil.notifyMessage(
                        this,
                        "Error",
                        commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_save_failed_http", { status: res.status })
                    );
                }
            } catch (error) {
                commonUtil.putExceptionErrorMessage(this, "savePrompt", error);
                const status = error && error.response && error.response.status;
                if (status) {
                    commonUtil.notifyMessage(
                        this,
                        "Error",
                        commonUtil.getMessageArgsText(this.$i18n, "error_ai_prompt_save_failed_http", { status })
                    );
                    return;
                }
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_ai_prompt_save_failed"));
            } finally {
                this.saving = false;
            }
        }
    },
    async mounted() {
        try {
            await this.loadAiServerUrl();
            await this.loadPromptList();
        } catch (error) {
            commonUtil.putExceptionErrorMessage(this, "mounted.loadAiServerUrl", error);
            commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_ai_server_url_failed"));
        }
    }
}
</script>

<style scoped>
.prompt-dialog-table {
    width: 100%;
}
.prompt-list-panel {
    width: 100%;
}

.object-titlebox {
    background-color: #f6f6f6;
}

.prompt-title-left {
    width: 50%;
    padding: 10px 0 10px 20px;
    text-align: left;
    vertical-align: middle;
}

.prompt-title-right {
    padding-right: 10px;
    text-align: right;
    vertical-align: middle;
}

.dialog-body :deep(.el-form) {
    margin: 0;
}

.dialog-body :deep(.el-form-item) {
    margin-bottom: 0;
}

.dialog-body :deep(.el-form-item__label) {
    line-height: normal;
    padding-bottom: 0;
}

.dialog-body :deep(.el-form-item__content) {
    line-height: normal;
}

.keyvalut-header-text {
    color: #848991;
    font-size: 12px;
    vertical-align: middle;
}

.icon-help {
    width: 14px;
    height: 14px;
    margin-left: 6px;
    vertical-align: middle;
    position: relative;
    top: -1px;
}

.tooltip-content {
    text-align: left;
}

.prompt-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.name-col {
    width: auto;
}

.op-col {
    width: 10%;
    text-align: center;
}

.prompt-header-row {
    width: 100%;
    padding: 10px 0 10px 28px;
    display: table-row;
    color: #848991;
    font-size: 12px;
    vertical-align: middle;
    background-color: #f6f6f6;
}

.prompt-header-cell {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 20px;
    font-size: 12px;
    font-weight: normal;
}

.prompt-header-cell.op-col {
    padding-left: 0;
    padding-right: 0;
    text-align: center;
}

.prompt-list-row {
    font-size: 13px;
}

.prompt-list-cell {
    border-bottom: 1px solid #e5e7ee;
    height: 42px;
    color: #4a4a4a;
    padding: 0 10px;
}

.prompt-list-cell.name-col {
    padding-left: 20px;
}

.op-cell {
    text-align: center;
    width: 10%;
    padding-left: 0;
    padding-right: 0;
}

.prompt-action-button {
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;
    line-height: 0;
}

.prompt-action-button:disabled {
    cursor: not-allowed;
    opacity: 0.35;
}

.prompt-action-button img {
    width: 14px;
    height: 14px;
}

.empty-row {
    text-align: center;
    color: #8f94a1;
}

.dialog-body {
    padding: 4px 4px 0;
}

.field-label {
    font-size: 13px;
    color: #3f4552;
    margin-bottom: 6px;
}

.with-top {
    margin-top: 16px;
}

.required {
    display: inline-block;
    margin-left: 6px;
    border-radius: 2px;
    font-size: 10px;
    line-height: 1;
    padding: 3px 5px;
    color: #fff;
    background: #e95d74;
}

.template-row {
    margin-bottom: 6px;
}

.template-label {
    font-size: 12px;
    color: #555b68;
    margin-right: 10px;
}

.template-help {
    font-size: 11px;
    color: #9aa0ac;
}

.template-select {
    width: 240px;
    margin-bottom: 10px;
}

.prompt-input-wrap {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    transition: border-color 0.2s ease;
}

.prompt-input-wrap:focus-within {
    border-color: #409eff;
}

.prompt-input-wrap :deep(.el-textarea__inner) {
    min-height: 360px !important;
    border: none;
    box-shadow: none;
    background: transparent;
}

.prompt-input-wrap :deep(.el-textarea__inner:focus) {
    border: none;
    box-shadow: none;
}

.prompt-input-wrap.has-error {
    border-color: #ef6b7b;
}

.prompt-input-wrap.has-error:focus-within {
    border-color: #ef6b7b;
}

.counter {
    text-align: right;
    font-size: 11px;
    color: #9aa0ac;
    margin-top: 4px;
}

.counter-error {
    color: #ef6b7b;
}

.error-text {
    color: #ef6b7b;
    font-size: 11px;
    margin-top: 2px;
}
</style>
